import { z } from "zod";
import { RoutineSchedule } from "./routine";

// ---------------------------------------------------------------------------
// Anser Planera
//
// Free text in ("Sänk svinnet i hela butiken med 1 procentenhet"), a plan per
// department out: a focus goal, the routines that reach it and the expected
// effect. `apply` writes goals and routines through their own entities.
// ---------------------------------------------------------------------------

export const PlanDepartment = z.object({
  salesTeamId: z.string().min(1),
  teamName: z.string().min(1),
  goal: z.object({
    kpiDefinitionId: z.string().min(1),
    kpiLabel: z.string().min(1),
    fromValue: z.number(),
    toValue: z.number(),
    byDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  }),
  routines: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        schedule: RoutineSchedule,
        requiresPhoto: z.boolean().default(false),
        ownerHint: z.string().max(120).nullable().default(null),
      }),
    )
    .min(1)
    .max(5),
  /** Why this department and this KPI, one or two sentences. */
  why: z.string().min(1).max(400),
  /** Expected yearly effect on the bottom line, in SEK; null when unknown. */
  effectSekPerYear: z.number().nullable().default(null),
});
export type PlanDepartment = z.infer<typeof PlanDepartment>;

export const PlanSuggestion = z.object({
  summary: z.string().min(1).max(600),
  departments: z.array(PlanDepartment).min(1).max(8),
  /** The report period the plan rests on, e.g. "v43". */
  basis: z.string().max(200).nullable().default(null),
});
export type PlanSuggestion = z.infer<typeof PlanSuggestion>;

export const SuggestPlanInput = z.object({
  businessId: z.string().min(1),
  goalText: z.string().min(3).max(500),
});
export type SuggestPlanInput = z.infer<typeof SuggestPlanInput>;

export const ApplyPlanInput = z.object({
  businessId: z.string().min(1),
  plan: PlanSuggestion,
  /** Apply only these departments; all when omitted. */
  salesTeamIds: z.array(z.string().min(1)).optional(),
});
export type ApplyPlanInput = z.infer<typeof ApplyPlanInput>;

export interface ApplyPlanResult {
  goalIds: string[];
  routineIds: string[];
}
